// Apolo IDE — client for the Radar API (/api/hermes/radar).
//
// Thin wrappers over apiFetch (auto-JWT), same idiom as hermesApiClient. The
// backend follows the house convention (raw success payloads; errors as
// `{ error, code }`), so each method unwraps one envelope level. Non-2xx
// throws an Error carrying `.status` + `.code` so callers can tell a domain
// 4xx (a rejected source/finding) apart from a real network failure.
//
// `makeRadarApi({ fetchImpl })` exists for tests (inject a fake apiFetch);
// `radarApi` is the default instance used by the app. Every route is
// user-scoped server-side (user_id = ? strictly) — the client passes nothing
// extra; the JWT is the tenant.

import { apiFetch } from '../lib/api.js';

export function makeRadarApi({ fetchImpl = apiFetch } = {}) {
  async function unwrap(path, opts) {
    const res = await fetchImpl(path, opts);
    if (!res.ok) {
      const e = new Error(res.error || `radar request failed (${res.status})`);
      e.status = res.status;
      e.code = res.code ?? (res.data && typeof res.data === 'object' ? res.data.code : undefined);
      throw e;
    }
    return res.data;
  }

  return {
    // ---- sources ----------------------------------------------------------
    //
    // A source = { id, kind, handle, url, lens, cadence, enabled, health,
    // lastScannedAt, lastError, createdAt, updatedAt }. kind is one of
    // github_repo|github_search|instagram|tiktok|skool; cadence is
    // daily|weekly|3xweek; health is ok|stale|failing (set by the scanner).

    /** List the user's sources. Unwraps {sources}. */
    async listSources() {
      const data = await unwrap('/api/hermes/radar/sources', { method: 'GET' });
      return data?.sources || [];
    },

    /** Create a source. body = { kind, handle, url?, lens?, cadence? }. Unwraps {source}. */
    async createSource({ kind, handle, url, lens, cadence } = {}) {
      const data = await unwrap('/api/hermes/radar/sources', {
        method: 'POST',
        body: { kind, handle, url, lens, cadence },
      });
      return data?.source;
    },

    /** Edit a source (PATCH). patch = { handle?, lens?, cadence?, enabled?, health? }. Unwraps {source}. */
    async updateSource(id, patch) {
      const data = await unwrap(`/api/hermes/radar/sources/${id}`, {
        method: 'PATCH',
        body: patch,
      });
      return data?.source;
    },

    /** Delete a source. Returns { ok }. */
    async deleteSource(id) {
      return unwrap(`/api/hermes/radar/sources/${id}`, { method: 'DELETE' });
    },

    // ---- findings ---------------------------------------------------------
    //
    // A finding = { id, sourceId, title, url, summary, score (S|A|B|C),
    // lensBecomes, lensProduct, lensEffort, tags (json), status
    // (new|saved|tasked|dispatched|dismissed), dedupeKey, createdAt,
    // updatedAt }. POST is an UPSERT by (user, dedupeKey) — the scanner can
    // re-emit a finding without creating duplicates.

    /** List findings. params = { status?, score? }. Unwraps {findings}. */
    async listFindings(params = {}) {
      const q = new URLSearchParams();
      if (params.status != null) q.set('status', params.status);
      if (params.score != null) q.set('score', params.score);
      const qs = q.toString();
      const data = await unwrap(`/api/hermes/radar/findings${qs ? `?${qs}` : ''}`, { method: 'GET' });
      return data?.findings || [];
    },

    /**
     * Create/upsert a finding. body = { sourceId?, title, url?, summary?, score?,
     * lens?, tags? }. Unwraps {finding}.
     *
     * apolo-improvement PROPOSALS (E2) come through this same route: radar_emit's
     * 'apolo_improvement' kind passes the proposal fields (isProposal/installState/
     * proposalSummary/installKind/installTarget) and the server stamps is_proposal=1
     * (findingUseCases.isProposalInput keys off exactly these). They MUST be
     * forwarded — dropping them silently downgrades a proposal to a plain finding,
     * so the Runs-tab proposal card never appears. Absent (plain finding) they stay
     * undefined and JSON-drop, so the server reads a non-proposal — no behavior
     * change for ordinary findings.
     */
    async createFinding({
      sourceId,
      title,
      url,
      summary,
      score,
      lens,
      tags,
      isProposal,
      installState,
      proposalSummary,
      installKind,
      installTarget,
    } = {}) {
      const data = await unwrap('/api/hermes/radar/findings', {
        method: 'POST',
        body: {
          sourceId,
          title,
          url,
          summary,
          score,
          lens,
          tags,
          isProposal,
          installState,
          proposalSummary,
          installKind,
          installTarget,
        },
      });
      return data?.finding;
    },

    /** Triage a finding (PATCH). patch = { status?, score?, lens? }. Unwraps {finding}. */
    async patchFinding(id, patch) {
      const data = await unwrap(`/api/hermes/radar/findings/${id}`, {
        method: 'PATCH',
        body: patch,
      });
      return data?.finding;
    },

    // ---- proposals --------------------------------------------------------
    //
    // A proposal = a finding flagged is_proposal=1: { id, ...finding fields,
    // proposalSummary, installKind (skill|plugin|code-pr), installTarget,
    // installState (proposed|scanning|installing|installed|dismissed|failed),
    // installRef }. The apolo-improvement queue Hermes surfaces for the operator
    // to install/apply. install/dismiss advance the install state machine
    // server-side and return the durable proposal.

    /** List proposals. params = { installState? }. Unwraps {proposals}. */
    async listProposals(params = {}) {
      const q = new URLSearchParams();
      if (params.installState != null) q.set('installState', params.installState);
      const qs = q.toString();
      const data = await unwrap(`/api/hermes/radar/proposals${qs ? `?${qs}` : ''}`, { method: 'GET' });
      return data?.proposals || [];
    },

    /** Install a proposal (state proposed -> scanning -> ...). Unwraps {proposal}. */
    async installProposal(id) {
      const data = await unwrap(`/api/hermes/radar/proposals/${id}/install`, { method: 'POST' });
      return data?.proposal;
    },

    /** Dismiss a proposal (state -> dismissed). Unwraps {proposal}. */
    async dismissProposal(id) {
      const data = await unwrap(`/api/hermes/radar/proposals/${id}/dismiss`, { method: 'POST' });
      return data?.proposal;
    },

    // ---- quarantine -------------------------------------------------------
    //
    // A quarantine item = { id, findingId, skillName, repoUrl, state
    // (pending|scanning|passed|failed|promoted), scanReport (json), createdAt,
    // updatedAt }. A scanned skill repo lands here before it can be promoted
    // into the shared skills table. promote moves state -> promoted.

    /** List quarantine items. Unwraps {items}. */
    async listQuarantine() {
      const data = await unwrap('/api/hermes/radar/quarantine', { method: 'GET' });
      return data?.items || [];
    },

    /** Quarantine a skill. body = { findingId?, skillName, repoUrl? }. Unwraps {item}. */
    async createQuarantine({ findingId, skillName, repoUrl } = {}) {
      const data = await unwrap('/api/hermes/radar/quarantine', {
        method: 'POST',
        body: { findingId, skillName, repoUrl },
      });
      return data?.item;
    },

    /** Promote a quarantined skill (state -> promoted). Unwraps {item}. */
    async promoteQuarantine(id) {
      const data = await unwrap(`/api/hermes/radar/quarantine/${id}/promote`, { method: 'POST' });
      return data?.item;
    },
  };
}

export const radarApi = makeRadarApi();
